import { useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from '../lib/api/apiClient';
import { useMyLeagueStore } from '../store/myLeagueStore';
import { useUserContext } from '../contexts/UserContext';

interface Player {
  id: number;
  first_name: string;
  last_name: string;
  position_1: string;
  position_2: string;
  position_3: string;
  mlb_team: string;
  image?: string;
  bats?: string;
  throws?: string;
  rule_five_eligibility?: string;
}

interface Contract {
  year_signed: number | null;
  current_salary: number;
  salaries: Array<{ year: number; salary: number }>;
  contract_types: Array<{ year: number; type: string }>;
  buyouts: Array<{ year: number; amount: number }>;
  total_value?: number; // Added for total contract value
}

interface RosterPlayer {
  id: number;
  rwPlayer: Player;
  level: string;
  status: string;
  status_until: string | null;
  playerContract: Contract;
  is_40_man: boolean;
  year: number;
  trade_status?: string | null;
  trade_status_until?: string | null;
}

interface ResearchFilters {
  name?: string;
  positions?: string[];
  levels?: string[];
  teams?: string[];
  page?: number;
  rowsPerPage?: number;
}

interface ResearchPlayersResponse {
  players: RosterPlayer[];
  totalCount: number;
  page: number;
  rowsPerPage: number;
}

interface UseTeamRosterReturn {
  players: RosterPlayer[];
  total_payroll: number;
  activeRosterSize: number | null;
  fortyManRosterSize: number | null;
  hasFranchiseTag: number | false | null;
  hasQualifyingOffer: boolean | null;
  rosterLocked: boolean;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  searchForPlayers: (leagueId: number, filters?: ResearchFilters) => Promise<ResearchPlayersResponse>;
  rosterMove: (playerId: number, transactionName: string) => Promise<unknown>;
}

export const useTeamRoster = (teamId: number): UseTeamRosterReturn => {
  const queryClient = useQueryClient();
  const { myTeam } = useUserContext();

  const fetchRoster = async () => {
    console.log('🔍 useTeamRoster fetchRoster called with teamId:', teamId);
    const response = await apiClient.get(`/roster/full-roster/${teamId}`);
    console.log('✅ useTeamRoster API response received for teamId:', teamId, response.data);
    return response.data;
  };

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['teamRoster', teamId],
    queryFn: fetchRoster,
    enabled: !!teamId && teamId !== 1,
    staleTime: 1000 * 60 * 5, // 5 minutes cache
    refetchOnWindowFocus: false,
  });

  const searchForPlayers = async (leagueId: number, filters: ResearchFilters = {}): Promise<ResearchPlayersResponse> => {
    try {
      const params: Record<string, unknown> = {
        ...filters,
        page: filters.page || 0,
        rowsPerPage: filters.rowsPerPage || 300
      };

      // Convert arrays to comma-separated strings for API
      if (params.positions && Array.isArray(params.positions)) {
        params.positions = (params.positions as string[]).join(',');
      }
      if (params.levels && Array.isArray(params.levels)) {
        params.levels = (params.levels as string[]).join(',');
      }
      if (params.teams && Array.isArray(params.teams)) {
        params.teams = (params.teams as string[]).join(',');
      }

      // Remove empty filters
      Object.keys(params).forEach(key => {
        if (params[key] === undefined || params[key] === '' || params[key] === null) {
          delete params[key];
        }
      });

      const response = await apiClient.get(`/roster/research/${leagueId}`, { params });
      return response.data;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to search players');
    }
  };

  const rosterMove = async (playerId: number, transactionName: string): Promise<unknown> => {
    try {
      // Get leagueId from store
      const myLeague = useMyLeagueStore.getState().myLeague;

      if (!myLeague?.id) {
        throw new Error('No league selected');
      }

      if (!myTeam) {
        throw new Error('No team selected');
      }

      const response = await apiClient.post('/roster/roster-move', {
        leagueId: myLeague.id,
        teamId: myTeam,
        playerId,
        transactionName
      });

      // Refresh the roster after a successful move
      await queryClient.invalidateQueries({ queryKey: ['teamRoster', teamId] });
      await refetch();

      return response.data;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Failed to make roster move');
    }
  };

  return {
    players: data?.players || [],
    total_payroll: data?.total_payroll || 0,
    activeRosterSize: data?.activeRosterSize ?? null,
    fortyManRosterSize: data?.fortyManRosterSize ?? null,
    hasFranchiseTag: data?.hasFranchiseTag !== undefined ? data.hasFranchiseTag : null,
    hasQualifyingOffer: data?.hasQualifyingOffer ?? null,
    rosterLocked: data?.locked ?? false,
    loading: isLoading,
    error: error instanceof Error ? error : (error ? new Error(String(error)) : null),
    refetch: async () => { await refetch(); },
    searchForPlayers,
    rosterMove
  };
}; 